import { Link } from "react-router-dom";
import { ArrowRight, FileDown } from "lucide-react";

export default function Brief() {
  return (
    <div className="brief-page" data-testid="approval-brief">
      <div className="brief-sheet">
        <p className="masthead-kicker">HARC Website Engagement — Approval Brief</p>
        <h1 className="brief-title" data-testid="brief-title">
          Keep the engine. Rebuild the experience.
        </h1>
        <p className="brief-lede">
          An independent audit of harcresearch.org confirms the existing WordPress platform is
          secure, maintained, and fully capable of supporting the approved redesign. We recommend
          retaining and modernizing it — and stopping short of a costly rebuild.
        </p>

        <div className="brief-grid">
          <section className="brief-block">
            <h2 className="brief-h2">What the audit found</h2>
            <ul>
              <li>WordPress on WP Engine + Cloudflare — managed, backed up, actively edited (Aug 2026)</li>
              <li>Working integrations worth keeping: Gravity Forms, Mailchimp, Qgiv donations, Rank Math SEO</li>
              <li>415 indexed URLs: 36 pages, 70 research projects, 48 people, 244 news items, 17 stale events</li>
              <li>The real gaps: a code-dependent theme, discipline-siloed navigation, no Impact Story model, no filterable resource library — all fixable in place</li>
            </ul>
          </section>

          <section className="brief-block">
            <h2 className="brief-h2">The scores (5 = most favorable)</h2>
            <table className="brief-table">
              <thead>
                <tr>
                  <th>Option</th>
                  <th>Score</th>
                </tr>
              </thead>
              <tbody>
                <tr className="brief-winner">
                  <td>A — Retain & modernize WordPress</td>
                  <td>29 / 35</td>
                </tr>
                <tr>
                  <td>B — New custom build (React/FastAPI/MongoDB)</td>
                  <td>20 / 35</td>
                </tr>
                <tr>
                  <td>C — Hybrid / headless</td>
                  <td>21 / 35</td>
                </tr>
              </tbody>
            </table>
            <p className="brief-note">
              Unverifiable factors (admin access, licenses, analytics) are flagged in the full
              recommendation and were not scored optimistically.
            </p>
          </section>

          <section className="brief-block">
            <h2 className="brief-h2">Why Option A</h2>
            <ul>
              <li>Lowest cost and migration risk — content stays in place; “migration” becomes curation, not export/import</li>
              <li>Nontechnical staff gain code-free publishing through standard WordPress blocks</li>
              <li>SEO equity preserved — same platform, URLs retained with a 301 map for what changes</li>
              <li>No new platform subscriptions and no permanent developer dependency</li>
            </ul>
          </section>

          <section className="brief-block">
            <h2 className="brief-h2">What approval sets in motion</h2>
            <ol>
              <li>Access verification (≈1 week) turns every UNVERIFIED flag into fact</li>
              <li>Content triage workshop decides migrate / archive / redirect / retire, item by item</li>
              <li>Phase 2 prototype: six approved components — then STOP for your review</li>
              <li>Phase 3 full build, WCAG 2.2 AA QA, staff training, launch prep; domain move stays deferred</li>
            </ol>
          </section>
        </div>

        <div className="brief-decision">
          <p>
            <strong>Decision requested:</strong> approve Option A — Retain and Modernize the
            existing WordPress platform — and authorize the access-verification step.
          </p>
          <p className="mt-3 text-sm text-[#F7F4EC]/85">
            Indicative investment: <strong>$18,000 – $25,000</strong> (prototype + full
            implementation; finalized after access verification and content triage).
          </p>
          <div className="brief-signoff">
            <span>Name / Role: ______________________</span>
            <span>Signature: ______________________</span>
            <span>Date: ____________</span>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-4">
          <a
            href="/HARC_Executive_Summary.pdf"
            download
            data-testid="brief-download-pdf"
            className="inline-flex items-center gap-2 bg-[#1F4636] px-6 py-3 text-xs font-bold uppercase tracking-[0.12em] text-white transition-colors hover:bg-[#2c624c]"
          >
            <FileDown size={15} strokeWidth={2.2} aria-hidden="true" /> Download Executive Summary
            (PDF)
          </a>
          <p className="brief-linkline !mt-0">
            Full 22-section recommendation with evidence and scoring rationale:{" "}
            <Link to="/" data-testid="brief-full-doc-link">
              read the Architecture Recommendation <ArrowRight size={13} aria-hidden="true" />
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
